export interface SpecialistLocationGatewayTerminalWriteLease {
  readonly epoch: number;
  readonly ownerKey: string;
  readonly token: symbol;
}

export interface SpecialistLocationGatewayTerminalTeardownLease {
  readonly epoch: number;
  readonly token: symbol;
}

let terminalWriteEpoch = 0;
let activeTeardownToken: symbol | null = null;
const activeWriteTokens = new Map<string, symbol>();

function safelyOwnsCurrentCustomer(isCurrentOwner: () => boolean) {
  try {
    return isCurrentOwner() === true;
  } catch {
    return false;
  }
}

/**
 * Synchronous lease for the marker -> immutable receipt transaction. Auth
 * teardown advances the epoch before clearing any customer-owned store, so a
 * re-entrant or late writer can only compensate its exact bytes.
 */
export function beginSpecialistLocationGatewayTerminalWrite(
  ownerKey: string,
  isCurrentOwner: () => boolean,
): SpecialistLocationGatewayTerminalWriteLease | null {
  const expectedEpoch = terminalWriteEpoch;
  if (
    activeTeardownToken ||
    activeWriteTokens.has(ownerKey) ||
    !safelyOwnsCurrentCustomer(isCurrentOwner)
  ) {
    return null;
  }
  if (
    activeTeardownToken ||
    terminalWriteEpoch !== expectedEpoch ||
    activeWriteTokens.has(ownerKey)
  ) {
    return null;
  }
  const token = Symbol("specialist-location-gateway-terminal-write");
  activeWriteTokens.set(ownerKey, token);
  return Object.freeze({ epoch: expectedEpoch, ownerKey, token });
}

export function specialistLocationGatewayTerminalWriteIsCurrent(
  lease: SpecialistLocationGatewayTerminalWriteLease,
  isCurrentOwner: () => boolean,
) {
  return (
    specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease) &&
    safelyOwnsCurrentCustomer(isCurrentOwner) &&
    specialistLocationGatewayTerminalWriteLeaseIsCurrent(lease)
  );
}

/** Pure post-callback frontier; deliberately invokes no caller-owned code. */
export function specialistLocationGatewayTerminalWriteLeaseIsCurrent(
  lease: SpecialistLocationGatewayTerminalWriteLease,
) {
  return (
    activeTeardownToken === null &&
    lease.epoch === terminalWriteEpoch &&
    activeWriteTokens.get(lease.ownerKey) === lease.token
  );
}

export function completeSpecialistLocationGatewayTerminalWrite(
  lease: SpecialistLocationGatewayTerminalWriteLease,
) {
  if (activeWriteTokens.get(lease.ownerKey) === lease.token) {
    activeWriteTokens.delete(lease.ownerKey);
  }
}

// Pre-draft resolution/Close mutations share the same auth-teardown epoch.
// These aliases intentionally reuse the proven synchronous lease rather than
// creating a second coordinator that teardown could advance out of order.
export const beginSpecialistLocationGatewayCustomerOwnedWrite =
  beginSpecialistLocationGatewayTerminalWrite;
export const specialistLocationGatewayCustomerOwnedWriteIsCurrent =
  specialistLocationGatewayTerminalWriteIsCurrent;
export const specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent =
  specialistLocationGatewayTerminalWriteLeaseIsCurrent;
export const completeSpecialistLocationGatewayCustomerOwnedWrite =
  completeSpecialistLocationGatewayTerminalWrite;

/**
 * Pure compensation frontier for target-exact deletion only. A teardown epoch
 * deliberately invalidates every create/replace lease, but the operation that
 * is still holding its token must remain able to erase only the exact bytes it
 * just recreated. Never use this authority to publish or restore customer
 * state.
 */
export function specialistLocationGatewayCustomerOwnedCleanupLeaseIsCurrent(
  lease: SpecialistLocationGatewayTerminalWriteLease,
) {
  return activeWriteTokens.get(lease.ownerKey) === lease.token;
}

/** Auth/session identity teardown must acquire this before its first barrier. */
export function beginSpecialistLocationGatewayTerminalTeardown(): SpecialistLocationGatewayTerminalTeardownLease {
  terminalWriteEpoch += 1;
  const token = Symbol("specialist-location-gateway-terminal-teardown");
  activeTeardownToken = token;
  return Object.freeze({ epoch: terminalWriteEpoch, token });
}

export function completeSpecialistLocationGatewayTerminalTeardown(
  lease: SpecialistLocationGatewayTerminalTeardownLease,
) {
  if (
    activeTeardownToken !== lease.token ||
    terminalWriteEpoch !== lease.epoch
  ) {
    return false;
  }
  activeTeardownToken = null;
  return true;
}
